import React, { useEffect, useMemo, useState } from "react";
import { fetchGrasslandAll, fetchRegions } from "@/lib/api";
import { toast } from "sonner";
import {
  Cow, Horse, Tree, Dog, Warning, CheckCircle, CircleNotch, Leaf, Fire,
  Code, Copy, ChartLineUp,
} from "@phosphor-icons/react";
import { StoryCard, ExplainPanel, MetricTooltip } from "@/components/Explain";
import GlossaryTerm from "@/components/GlossaryTerm";

const ANIMALS = [
  { id: "dairy_cow",   label: "Milchkuh", icon: Cow },
  { id: "horse",       label: "Pferd",    icon: Horse },
  { id: "beef_cattle", label: "Mastrind", icon: Cow },
  { id: "sheep",       label: "Schaf",    icon: Tree },
  { id: "goat",        label: "Ziege",    icon: Dog },
];

const TONE_CLASSES = {
  ok:     "bg-[#00FA9A]/10 text-[#00FA9A] border-[#00FA9A]/40",
  warn:   "bg-[#FFD700]/10 text-[#E8CB6B] border-[#FFD700]/40",
  danger: "bg-[#FF5500]/10 text-[#FF7A45] border-[#FF5500]/40",
};

const FRUCTAN_TONE = {
  0: { label: "Niedrig",    cls: "bg-[#00FA9A]/10 text-[#00FA9A]", icon: CheckCircle },
  1: { label: "Mäßig",      cls: "bg-white/5 text-zinc-400", icon: Warning },
  2: { label: "Erhöht",     cls: "bg-[#FFD700]/10 text-[#E8CB6B]", icon: Warning },
  3: { label: "Kritisch",   cls: "bg-[#FF5500]/10 text-[#FF7A45]", icon: Fire },
};

export default function Grassland() {
  const [items, setItems] = useState(null);
  const [regions, setRegions] = useState([]);
  const [animal, setAnimal] = useState("dairy_cow");
  const [regionFilter, setRegionFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchGrasslandAll(), fetchRegions()])
      .then(([g, r]) => {
        if (!cancelled) {
          setItems(g.items || []);
          setRegions(r || []);
        }
      })
      .catch(() => toast.error("Weide-Analyse konnte nicht geladen werden"));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = regionFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.region_label || "").toLowerCase().includes(q) ||
      (it.bundesland || "").toLowerCase().includes(q)
    );
  }, [items, regionFilter]);

  return (
    <div className="space-y-6" data-testid="grassland-root">
      <header>
        <div className="eid-label">Weide &amp; Futter · Pro Tiergruppe</div>
        <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tighter mt-1 leading-[0.95]">
          Grünland- &amp; Futterqualität
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
          Wie nährhaft ist das Gras gerade auf der Weide? Bodenblick schätzt aus
          Satellit, Wetter und Bodenfeuchte zwei Werte: Rohprotein (das „Wachstums-Eiweiß")
          und Energie (die „Tankfüllung"). Plus eine Fruktan-Warnung für Pferde.
        </p>
      </header>

      <StoryCard
        eyebrow="Wozu Weide-Analysen?"
        title="Drei Werte, eine Entscheidung: Wann zufüttern, wann austreiben?"
        lead="Wähle deine Tierart oben. Bodenblick zeigt dir für jede Region des Voralpenlandes die heutige Futter-Qualität — und ob ein Risiko (z. B. Fruktan beim Pferd, niedrige Energie bei Milchkühen) vorliegt."
        bullets={[
          "Rohprotein (%) = Wachstums-Eiweiß. Junges Gras: 18–25%, alter Heuschnitt: <10%",
          "Energie (MJ NEL/kg TM) = was das Tier daraus an Milch/Fleisch machen kann",
          "Fruktan = Pferde-Killer. Bei Stress (Frost+Sonne) gefährlich für Hufrehe.",
        ]}
        accent="#D9A87C"
        icon={ChartLineUp}
        testid="grassland-story"
      />

      <ExplainPanel
        title="Wie kommt Bodenblick auf diese Werte?"
        accent="#D9A87C"
        testid="grassland-explain"
      >
        <p>
          Bodenblick misst <strong>kein einzelnes Grashalm</strong>. Aber Satelliten-Indizes (<GlossaryTerm word="NDVI">NDVI</GlossaryTerm>, <GlossaryTerm word="NDWI">NDWI</GlossaryTerm>, <GlossaryTerm word="GNDVI">GNDVI</GlossaryTerm>) korrelieren stark mit Biomasse, Wassergehalt und Chlorophyll-Dichte. Aus diesen Werten plus <GlossaryTerm word="Bodenfeuchte">Bodenfeuchte</GlossaryTerm> und Temperatur der letzten 14 Tage schätzen wir:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><GlossaryTerm word="Rohprotein">Rohprotein</GlossaryTerm> aus NDVI-Höhe + Wachstumstempo (junges Gras = höher).</li>
          <li>Energie (<GlossaryTerm word="NEL">NEL</GlossaryTerm>) aus NDVI + Strahlung der letzten 7 Tage.</li>
          <li><GlossaryTerm word="Fruktan">Fruktan</GlossaryTerm>-Risiko aus kalten Nächten (&lt;5 °C) und sonnigen Tagen — der „Pferde-Killer-Mix".</li>
        </ul>
        <p className="text-xs text-zinc-400 mt-2 pt-2 border-t border-white/15">
          ⚠ Bodenblick ersetzt keine Heu-Analyse vom Labor. Für genaue Rationen-Berechnung
          empfehlen wir bei kritischen Werten zusätzlich eine LK-/LWG-Futtermittel-Probe.
        </p>
      </ExplainPanel>

      {/* Animal selector */}
      <div className="flex flex-wrap gap-2" data-testid="grassland-animal-selector">
        {ANIMALS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setAnimal(id)}
            data-testid={`grassland-animal-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all ${
              animal === id
                ? "bg-[#00FA9A] text-black border-[#00FA9A]/40 shadow-sm"
                : "bg-[#121212] border-white/10 hover:bg-white/5"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Fructan spotlight for horses */}
      {animal === "horse" && <HorseFructanSpotlight items={filtered} />}
      {animal === "horse" && <EmbedCodePanel items={filtered} />}

      {/* Region filter */}
      <div className="eid-card p-3 flex items-center gap-2">
        <Leaf size={16} className="text-[#00FA9A]" />
        <input
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          placeholder="Region oder Bundesland filtern…"
          data-testid="grassland-region-filter"
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-zinc-500"
        />
        <span className="text-[10px] font-mono text-zinc-500">
          {filtered ? filtered.length : "–"} Regionen
        </span>
      </div>

      {/* Loading */}
      {!filtered && (
        <div className="eid-card p-8 flex items-center justify-center gap-2 text-zinc-400">
          <CircleNotch size={18} className="animate-spin" /> Lade Weide-Analyse…
        </div>
      )}

      {/* Region cards */}
      {filtered && (
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="grassland-cards">
          {filtered.map((it) => (
            <GrasslandCard key={it.region_id} item={it} animal={animal} />
          ))}
        </section>
      )}

      {/* Disclaimer */}
      <div className="text-xs text-zinc-400 border-l-2 border-[#FFD700]/40 pl-3 py-1">
        Orientierungswerte aus Fernerkundung &amp; Wettermodellen — ersetzen
        keine Laboranalyse des Futters. Alle Werte ohne Gewähr.
      </div>
    </div>
  );
}

function HorseFructanSpotlight({ items }) {
  if (!items) return null;
  const critical = items.filter((i) => i.fructan_risk?.score >= 2);
  if (critical.length === 0) {
    return (
      <div className="eid-card p-4 border-l-4 border-[#00FA9A]/40 bg-[#00FA9A]/10" data-testid="grassland-fructan-ok">
        <div className="flex items-center gap-2">
          <CheckCircle size={18} weight="fill" className="text-[#00FA9A]" />
          <div className="text-sm font-medium">Alle Regionen aktuell unkritisch für Pferde</div>
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          Kein erhöhtes Fruktan-Risiko in den überwachten Gebieten. Trotzdem:
          empfindliche Tiere nach Frostnächten weiterhin beobachten.
        </p>
      </div>
    );
  }
  return (
    <div className="eid-card p-4 border-l-4 border-[#FF5500]/40 bg-[#FF5500]/10" data-testid="grassland-fructan-alert">
      <div className="flex items-center gap-2">
        <Fire size={18} weight="fill" className="text-[#FF7A45]" />
        <div className="text-sm font-medium">
          Fruktan-Warnung aktiv in {critical.length} Region{critical.length > 1 ? "en" : ""}
        </div>
      </div>
      <p className="text-xs text-[#E8CB6B] mt-1.5 leading-relaxed">
        Rehe-gefährdete Pferde heute nur stark begrenzt oder gar nicht auf die
        Weide lassen. Kraftfutter & Heu-Rationierung prüfen, Weidegang wenn
        möglich in die frühen Morgenstunden (kühler Boden) verlegen.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {critical.slice(0, 8).map((c) => (
          <span
            key={c.region_id}
            className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#121212] border border-[#FF5500]/40 text-[#FF7A45]"
          >
            {c.region_label}
          </span>
        ))}
        {critical.length > 8 && (
          <span className="text-[10px] font-mono text-[#FF7A45] self-center">
            +{critical.length - 8} weitere…
          </span>
        )}
      </div>
    </div>
  );
}

function GrasslandCard({ item, animal }) {
  const ani = item.animals?.[animal];
  const fructan = item.fructan_risk || {};
  const ft = FRUCTAN_TONE[fructan.score ?? 0];
  const FIcon = ft.icon;
  const toneCls = TONE_CLASSES[ani?.tone || "ok"];
  return (
    <article className="eid-card p-4 space-y-3" data-testid={`grassland-card-${item.region_id}`}>
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            {item.country} · {item.bundesland}
          </div>
          <h3 className="text-base font-medium tracking-tight">{item.region_label}</h3>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${ft.cls}`}>
          <FIcon size={10} weight="fill" /> Fruktan: {ft.label}
        </span>
      </header>

      {/* Nutrition numbers */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <NutritionCell label="Protein" value={item.nutrition?.crude_protein_pct} unit="% TM" />
        <NutritionCell label="NEL" value={item.nutrition?.nel_mj_per_kg_dm} unit="MJ/kg" />
        <NutritionCell label="Aufwuchs" value={item.nutrition?.biomass_index} unit="/100" />
      </div>

      {/* Animal-specific messages */}
      <div className={`border-l-3 rounded-sm px-3 py-2 ${toneCls}`} style={{ borderLeftWidth: 3 }}>
        <div className="text-[10px] font-mono uppercase tracking-wider">
          Hinweis für {ani?.label}
        </div>
        <ul className="mt-1 space-y-1 text-xs leading-snug">
          {(ani?.messages || []).map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      </div>

      {/* Fructan reasons */}
      {fructan.reasons?.length > 0 && fructan.score >= 1 && (
        <details className="text-xs" data-testid={`grassland-fructan-reasons-${item.region_id}`}>
          <summary className="cursor-pointer text-zinc-400 hover:text-white">
            Warum dieses Fruktan-Risiko?
          </summary>
          <ul className="mt-1.5 ml-3 space-y-0.5 text-zinc-400">
            {fructan.reasons.map((r, i) => (
              <li key={i}>– {r}</li>
            ))}
          </ul>
        </details>
      )}
    </article>
  );
}

function NutritionCell({ label, value, unit }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2">
      <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-mono font-medium mt-0.5">
        {value ?? "–"}
        <span className="text-[9px] text-zinc-500 ml-1">{unit}</span>
      </div>
    </div>
  );
}

/**
 * Panel zum Generieren eines iframe-Embed-Codes für Reitställe &
 * Pferde-Communities. Liest die aktuelle Origin und bietet copy-to-clipboard.
 */
function EmbedCodePanel({ items }) {
  const [regionId, setRegionId] = useState("");
  const [width, setWidth] = useState(420);
  const [height, setHeight] = useState(520);

  // Set default region on first mount once items are loaded.
  useEffect(() => {
    if (items && items.length > 0 && !regionId) setRegionId(items[0].region_id);
  }, [items, regionId]);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://bodenblick.com";
  const embedUrl = regionId ? `${origin}/widget/fructan/${regionId}` : "";
  const embedCode = regionId
    ? `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="0" style="border:1px solid #E2DFD8;border-radius:2px;max-width:100%;" title="Fruktan-Warnung · Bodenblick" loading="lazy"></iframe>`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      toast.success("Embed-Code kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen – bitte manuell markieren.");
    }
  };

  return (
    <div className="eid-card p-4 space-y-3" data-testid="grassland-embed-panel">
      <div className="flex items-start gap-3">
        <span className="shrink-0 p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
          <Code size={18} weight="bold" />
        </span>
        <div className="flex-1">
          <h3 className="text-base font-medium">Fruktan-Widget einbetten</h3>
          <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">
            Teile die Fruktan-Warnung auf deiner Website, im Reitstall-Blog oder
            in Pferde-Communities. Kein Login nötig — nur ein{" "}
            <code className="font-mono text-[11px]">&lt;iframe&gt;</code>-Snippet.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            Region
          </span>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            data-testid="grassland-embed-region"
            className="mt-1 w-full bg-[#121212] border border-white/10 rounded-sm px-2 py-1.5 text-sm"
          >
            {(items || []).map((it) => (
              <option key={it.region_id} value={it.region_id}>
                {`${it.region_label} (${it.country})`}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            Breite (px)
          </span>
          <input
            type="number" min="280" max="800" step="10"
            value={width}
            onChange={(e) => setWidth(Math.max(280, Math.min(800, +e.target.value || 420)))}
            data-testid="grassland-embed-width"
            className="mt-1 w-full bg-[#121212] border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            Höhe (px)
          </span>
          <input
            type="number" min="380" max="720" step="10"
            value={height}
            onChange={(e) => setHeight(Math.max(380, Math.min(720, +e.target.value || 520)))}
            data-testid="grassland-embed-height"
            className="mt-1 w-full bg-[#121212] border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono"
          />
        </label>
      </div>

      <div className="relative">
        <pre
          className="bg-white/10 text-[#DCE5D5] text-[11px] leading-snug p-3 rounded-sm overflow-x-auto whitespace-pre-wrap break-all font-mono"
          data-testid="grassland-embed-code"
        >
          {embedCode || "Lade Regionen…"}
        </pre>
        <button
          onClick={copy}
          disabled={!embedCode}
          data-testid="grassland-embed-copy"
          className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-[#00FA9A] hover:bg-[#33FBAE] disabled:opacity-40 text-white rounded-sm px-2.5 py-1 text-[11px] font-medium"
        >
          <Copy size={12} weight="bold" /> Kopieren
        </button>
      </div>

      {embedUrl && (
        <a
          href={embedUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="grassland-embed-preview"
          className="inline-block text-xs font-mono text-[#00FA9A] underline hover:text-[#00FA9A]"
        >
          → Vorschau in neuem Tab öffnen
        </a>
      )}
    </div>
  );
}
